import { makeAutoObservable } from 'mobx'
import { isCancel } from 'axios'
import { RequestCancelAxios } from 'shared/api/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { CallsAnalyticsApi } from 'entities/Analytics/api/callsAnalytics'
import { type CallsAnalyticsKeys } from 'entities/Analytics/api/types'
import { RowCallsComparison } from 'entities/Analytics/model/Table/RowCallsComparison'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { CallsChartDataStore } from 'pages/analytics/pages/calls/ui/Chart'
import { AnalyticsFiltersStore } from 'pages/analytics/store/analyticsFiltersStore'
import { ExportAnalyticsModalStore } from 'pages/analytics/ui/ExportAnalyticsModal'
import {
  AnalyticsComparisonTableStore,
  type IAnalyticsComparisonTableConfig,
} from 'pages/analytics/store/analyticsComparisonTableStore'

export type CallsComparisonTableStore = AnalyticsComparisonTableStore<
  CallsAnalyticsKeys,
  RowCallsComparison
>

export type ICallsComparisonTableConfig = IAnalyticsComparisonTableConfig<
  CallsAnalyticsKeys,
  RowCallsComparison
>

export class CallsAnalyticsPageStore {
  private readonly _chartDataStore: CallsChartDataStore
  private readonly _analyticsFiltersStore: AnalyticsFiltersStore
  private readonly _exportAnalyticsModalStore: ExportAnalyticsModalStore
  private readonly _comparisonTableStore: CallsComparisonTableStore
  private readonly _requestCancelAxios = new RequestCancelAxios()

  private _error: string | null = null
  private _pageLoading = false
  private _chartLoading = false

  constructor(entities: IEntitiesStore) {
    this._chartDataStore = new CallsChartDataStore()
    this._analyticsFiltersStore = new AnalyticsFiltersStore(entities, {
      onFiltersChange: (loadingType) => this._fetchAnalytics(loadingType),
      additionalFilterTypes: [
        SearchTypesEnum.members,
        SearchTypesEnum.teams,
        SearchTypesEnum.numbers,
        SearchTypesEnum.tags,
        SearchTypesEnum.time_range,
      ],
      breakdownFilterTypes: [
        SearchTypesEnum.members,
        SearchTypesEnum.teams,
        SearchTypesEnum.numbers,
        SearchTypesEnum.tags,
      ],
    })
    this._exportAnalyticsModalStore = new ExportAnalyticsModalStore(
      CallsAnalyticsApi.exportAnalytics
    )

    const comparisonTableConfig: ICallsComparisonTableConfig = {
      tableKey: 'callsComparisonTable',
      getAnalytics: CallsAnalyticsApi.getAnalytics,
      createRow: (data) => new RowCallsComparison(data),
      createMetricsRecord: () => ({
        total: 0,
        outbound_calls: 0,
        inbound_calls: 0,
        avg_duration_outbound: 0,
        answered_outbound: 0,
        unanswered_outbound: 0,
        busy_outbound: 0,
        failed_outbound: 0,
        avg_duration_inbound: 0,
        answered_inbound_human: 0,
        answered_inbound_ai: 0,
        missed_inbound: 0,
        busy_inbound: 0,
        failed_inbound: 0,
        forwarded: 0,
        avg_duration_forwarded: 0,
      }),
    }

    this._comparisonTableStore = new AnalyticsComparisonTableStore(
      comparisonTableConfig,
      () => this._analyticsFiltersStore.analyticsFiltersParams,
      () => this._analyticsFiltersStore.breakdownFilterStore.appliedBreakdownFilterType,
      entities
    )
    makeAutoObservable(this)
  }

  init = () => {
    this._analyticsFiltersStore.initReactions()
    this._comparisonTableStore.init()
    this._fetchAnalytics('page')
  }

  openExportModal = () => {
    const { analyticsFiltersParams } = this._analyticsFiltersStore
    this._exportAnalyticsModalStore.onOpenExport(analyticsFiltersParams)
  }

  private _fetchAnalytics = async (loadingType: 'page' | 'chart' = 'page') => {
    if (loadingType === 'page') {
      this._pageLoading = true
    } else {
      this._chartLoading = true
    }

    try {
      this._error = null

      const { analyticsFiltersParams } = this._analyticsFiltersStore
      const { period } = this._analyticsFiltersStore.chartFilterStore

      this._requestCancelAxios.init()

      await CallsAnalyticsApi.preloadAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      const response = await CallsAnalyticsApi.getAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      this._chartDataStore.setRawData(response.data, period)
    } catch (error) {
      if (!isCancel(error)) {
        console.error('Analytics fetch error:', error)
        this._error = 'Failed to load analytics data'
      }
    } finally {
      if (loadingType === 'page') {
        this._pageLoading = false
      } else {
        this._chartLoading = false
      }
    }
  }

  get calendarFilterStore() {
    return this._analyticsFiltersStore.calendarFilterStore
  }

  get breakdownFilterStore() {
    return this._analyticsFiltersStore.breakdownFilterStore
  }

  get chartFilterStore() {
    return this._analyticsFiltersStore.chartFilterStore
  }

  get chartDataStore() {
    return this._chartDataStore
  }

  get comparisonTableStore() {
    return this._comparisonTableStore
  }

  get pageLoading() {
    return this._pageLoading
  }

  get chartLoading() {
    return this._chartLoading
  }

  get error() {
    return this._error
  }

  dispose = () => {
    this._analyticsFiltersStore.dispose()
    this._requestCancelAxios.reset()
    this._chartDataStore.dispose?.()
    this._comparisonTableStore.dispose()
    this._exportAnalyticsModalStore.dispose()
  }
}
